<script setup>
import { computed } from "vue";
import { usePage } from "@inertiajs/vue3";
import BrandHeader from "./Partials/BrandHeader.vue";
import BrandFooter from "./Partials/BrandFooter.vue";
import DashboardHeader from "./Partials/DashboardHeader.vue";

/**
 * Chrome for the redesigned secondary pages (GH-1121: Privacy, Terms,
 * Contact). Like ExploreLayout, the header follows the session — guests get
 * the public BrandHeader, members the dashboard header — over a shared
 * gradient hero band, a white content card and the brand footer.
 */
defineProps({
    title: {
        type: String,
        required: true,
    },
    subtitle: {
        type: String,
        default: null,
    },
    // Set false when the page supplies its own art over the band (the Mobile
    // App phones), so the people collage stays out of the way.
    showHeroArt: {
        type: Boolean,
        default: true,
    },
    // Extra classes for the title, e.g. a max-width so it wraps around art.
    titleClass: {
        type: String,
        default: null,
    },
});

const isAuthed = computed(() => !!usePage().props.auth?.user);
</script>

<template>
    <div class="brand min-h-screen bg-brand-cream font-brand text-brand-navy">
        <BrandHeader v-if="!isAuthed" />
        <div v-else class="px-5 pt-5">
            <div class="mx-auto max-w-[1472px]">
                <DashboardHeader />
            </div>
        </div>

        <!--
            The footer supplies the card's rounded bottom (see DashboardLayout),
            so the white body carries no radius of its own.
        -->
        <main class="px-5">
            <div class="mx-auto max-w-[1472px] bg-white">
                <!--
                    The #hero slot lets a page replace the whole band (About
                    ships a taller one with its own collage); the default is
                    the shared 171px title band.
                -->
                <slot name="hero">
                    <section
                        class="relative flex min-h-[171px] items-center overflow-hidden border-t border-brand-tan/40 bg-gradient-to-r from-brand-red to-[#ffb545]"
                    >
                        <!--
                            People collage (flat Figma export, 742×171 @1x).
                            Sits flush right; from lg it rides the bottom edge
                            at 110px so it clears the title, full height from
                            xl where the column and the art no longer overlap.
                        -->
                        <img
                            v-if="showHeroArt"
                            :src="
                                asset('images/brand/secondary-hero-people.webp')
                            "
                            alt=""
                            aria-hidden="true"
                            class="pointer-events-none absolute bottom-0 right-0 h-[110px] w-auto max-w-none max-lg:hidden xl:h-full"
                        />

                        <div
                            class="relative mx-auto w-full max-w-[983px] px-5 py-10 md:py-12"
                        >
                            <h1
                                class="font-brand-display text-[36px] font-black italic uppercase leading-[0.87] tracking-[-0.02em] text-white md:text-[45px]"
                                :class="titleClass"
                            >
                                {{ title }}
                            </h1>
                            <p
                                v-if="subtitle"
                                class="mt-2 text-[16px] font-semibold leading-[1.3] text-[#ffe5bf]"
                            >
                                {{ subtitle }}
                            </p>
                        </div>
                    </section>
                </slot>

                <div
                    class="mx-auto max-w-[983px] px-5 pb-24 pt-12 md:pb-40 md:pt-20"
                >
                    <slot />
                </div>
            </div>
        </main>

        <BrandFooter />
    </div>
</template>
